import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Asphalt Coring",
  "Concrete Coring",
  "Geotechnical Boring",
  "Environmental Boring",
  "Soil Sampling / Subsurface Investigation",
  "Utility & Pavement Investigation Support",
  "Other / Not Sure",
];

const initial = {
  name: "",
  company: "",
  phone: "",
  email: "",
  project_address: "",
  service: "",
  quantity: "",
  depth: "",
  preferred_date: "",
  description: "",
};

const inputCls =
  "w-full bg-white border border-slate-300 rounded-sm px-4 py-3 text-base text-[#0F172A] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#FF5A00] focus:border-[#FF5A00] transition-shadow duration-200";

const labelCls = "block text-xs font-bold uppercase tracking-wider text-[#0F172A] mb-2";

export default function QuoteForm() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Quote request received! We'll be in touch within one business day.");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please call us at (832) 661-1121.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" className="py-24 lg:py-32 bg-[#F4F5F7]" data-testid="quote-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-4"
          >
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#FF5A00] mb-4 font-mono-tech" data-testid="quote-overline">
              Request a Quote • 07
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-black uppercase tracking-tight text-[#0F172A]" data-testid="quote-heading">
              Get Project Pricing
            </h2>
            <p className="mt-6 text-base text-slate-600 leading-relaxed">
              Tell us about your project and we'll respond with a scope-based quote, typically within one business
              day. Pricing depends on project scope, depth, soil conditions, site access, and location.
            </p>
            <p className="mt-6 text-base text-slate-600 leading-relaxed">
              Need it faster? Call us directly at{" "}
              <a href="tel:+18326611121" className="font-semibold text-[#FF5A00] hover:text-[#E04F00]" data-testid="quote-call-link">
                (832) 661-1121
              </a>
              .
            </p>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5 }}
            onSubmit={submit}
            className="lg:col-span-8 bg-white border border-slate-200 rounded-sm p-8 lg:p-10"
            data-testid="quote-form"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="q-name" className={labelCls}>Name *</label>
                <input id="q-name" required value={form.name} onChange={set("name")} className={inputCls} placeholder="John Smith" data-testid="quote-input-name" />
              </div>
              <div>
                <label htmlFor="q-company" className={labelCls}>Company</label>
                <input id="q-company" value={form.company} onChange={set("company")} className={inputCls} placeholder="ABC Engineering" data-testid="quote-input-company" />
              </div>
              <div>
                <label htmlFor="q-phone" className={labelCls}>Phone *</label>
                <input id="q-phone" required type="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="(713) 555-0000" data-testid="quote-input-phone" />
              </div>
              <div>
                <label htmlFor="q-email" className={labelCls}>Email *</label>
                <input id="q-email" required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@company.com" data-testid="quote-input-email" />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="q-address" className={labelCls}>Project Address *</label>
                <input id="q-address" required value={form.project_address} onChange={set("project_address")} className={inputCls} placeholder="Street, City, TX ZIP" data-testid="quote-input-address" />
              </div>
              <div>
                <label htmlFor="q-service" className={labelCls}>Requested Service *</label>
                <select id="q-service" required value={form.service} onChange={set("service")} className={inputCls} data-testid="quote-select-service">
                  <option value="" disabled>Select a service</option>
                  {SERVICES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="q-quantity" className={labelCls}>Number of Cores / Borings</label>
                <input id="q-quantity" value={form.quantity} onChange={set("quantity")} className={inputCls} placeholder="e.g., 6 cores" data-testid="quote-input-quantity" />
              </div>
              <div>
                <label htmlFor="q-depth" className={labelCls}>Required Depth</label>
                <input id="q-depth" value={form.depth} onChange={set("depth")} className={inputCls} placeholder="e.g., 20 ft" data-testid="quote-input-depth" />
              </div>
              <div>
                <label htmlFor="q-date" className={labelCls}>Preferred Date</label>
                <input id="q-date" type="date" value={form.preferred_date} onChange={set("preferred_date")} className={inputCls} data-testid="quote-input-date" />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="q-desc" className={labelCls}>Project Description *</label>
                <textarea id="q-desc" required rows={4} value={form.description} onChange={set("description")} className={inputCls} placeholder="Tell us about site access, soil conditions, deliverables, schedule, etc." data-testid="quote-input-description" />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="quote-submit-button"
              className="mt-8 inline-flex items-center justify-center gap-2 bg-[#FF5A00] hover:bg-[#E04F00] disabled:opacity-60 text-white font-heading font-bold uppercase tracking-wider px-10 py-4 rounded-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[5px_5px_0px_#0F172A]"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              {loading ? "Sending..." : "Submit Quote Request"}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
